import {
  TRANSLATE,
  METRIC_TYPE_OPTIONS_TRANSLATE,
  TARGET_OPTIONS_TRANSLATE,
  UNIT_TRANSLATE,
  CATEGORY_OPTIONS_TRANSLATE
} from 'constants/translate'
import { ISettingKPITable } from 'models/client/kpi-setting'
import { IKPIDetail } from 'models/kpi'
import NumberUtil from './number'
import { EEntity, EKPICategory, EKPIMetricType, EKPITargetType, EKPIUnit } from 'enums/kpi'

type TValue = number | undefined

export class QuarterCalculator {
  quarterValues: [TValue, TValue, TValue, TValue]

  constructor(q1: TValue, q2: TValue, q3: TValue, q4: TValue) {
    this.quarterValues = [q1, q2, q3, q4]
  }

  private isUndefined(value: number | undefined | '') {
    return value === undefined || value === null || value === ''
  }

  private getNonEmptyQuarters() {
    return this.quarterValues.filter((v) => !this.isUndefined(v))
  }

  max() {
    const nonEmptyQuarters = this.getNonEmptyQuarters()
    return Math.max(...(nonEmptyQuarters as number[]))
  }

  isAtLeastOneQuarterValueEntered() {
    return this.quarterValues.some((v) => !this.isUndefined(v))
  }

  isOnlyOneQuarterValueEntered() {
    const nonEmptyQuarters = this.getNonEmptyQuarters()

    return nonEmptyQuarters.length === 1
  }

  areQuarterValuesEqual() {
    const nonEmptyQuarters = this.getNonEmptyQuarters()

    return nonEmptyQuarters.every((v, _i, arr) => v === arr[0])
  }
}

const isNumber = (num: string | number | undefined) => {
  if (typeof num === 'number' && !Number.isNaN(num)) return true
  return false
}

export const transformApiKpiDetailToSettinKpiData = (data: IKPIDetail[]): ISettingKPITable[] =>
  data.map((item) => {
    const unit = UNIT_TRANSLATE[item.unit as EKPIUnit]
    const options = { suffix: '', prefix: '' }

    if (item.unit === EKPIUnit.PERCENT) {
      options.suffix = unit
    } else if (item.unit === EKPIUnit.SGD) {
      options.prefix = unit
    }

    return [
      {
        data: item,
        unit: item.unit,
        metric: item.metric,
        entity: item.entity,
        metricType: METRIC_TYPE_OPTIONS_TRANSLATE[item.metricType as EKPIMetricType],
        targetType: TARGET_OPTIONS_TRANSLATE[item.targetType as EKPITargetType],
        category: CATEGORY_OPTIONS_TRANSLATE[item.category as EKPICategory],
        baseline: isNumber(item.baseline) ? NumberUtil.format(item.baseline, options) : TRANSLATE.NA,
        target: isNumber(item.target) ? NumberUtil.format(item.target, options) : TRANSLATE.NA,
        comment: item.comment
      },
      {
        data: null,
        unit: item.unit,
        metric: 'Q1',
        entity: EEntity.NORMAL,
        metricType: METRIC_TYPE_OPTIONS_TRANSLATE[item.metricType as EKPIMetricType],
        targetType: TARGET_OPTIONS_TRANSLATE[item.targetType as EKPITargetType],
        category: CATEGORY_OPTIONS_TRANSLATE[item.category as EKPICategory],
        baseline: isNumber(item.baselineQ1) ? NumberUtil.format(item.baselineQ1, options) : TRANSLATE.NA,
        target: isNumber(item.targetQ1) ? NumberUtil.format(item.targetQ1, options) : TRANSLATE.NA,
        comment: ''
      },
      {
        data: null,
        unit: item.unit,
        metric: 'Q2',
        entity: EEntity.NORMAL,
        metricType: METRIC_TYPE_OPTIONS_TRANSLATE[item.metricType as EKPIMetricType],
        targetType: TARGET_OPTIONS_TRANSLATE[item.targetType as EKPITargetType],
        category: CATEGORY_OPTIONS_TRANSLATE[item.category as EKPICategory],
        baseline: isNumber(item.baselineQ2) ? NumberUtil.format(item.baselineQ2, options) : TRANSLATE.NA,
        target: isNumber(item.targetQ2) ? NumberUtil.format(item.targetQ2, options) : TRANSLATE.NA,
        comment: ''
      },
      {
        data: null,
        unit: item.unit,
        metric: 'Q3',
        entity: EEntity.NORMAL,
        metricType: METRIC_TYPE_OPTIONS_TRANSLATE[item.metricType as EKPIMetricType],
        targetType: TARGET_OPTIONS_TRANSLATE[item.targetType as EKPITargetType],
        category: CATEGORY_OPTIONS_TRANSLATE[item.category as EKPICategory],
        baseline: isNumber(item.baselineQ3) ? NumberUtil.format(item.baselineQ3, options) : TRANSLATE.NA,
        target: isNumber(item.targetQ3) ? NumberUtil.format(item.targetQ3, options) : TRANSLATE.NA,
        comment: ''
      },
      {
        data: null,
        unit: item.unit,
        metric: 'Q4',
        entity: EEntity.NORMAL,
        metricType: METRIC_TYPE_OPTIONS_TRANSLATE[item.metricType as EKPIMetricType],
        targetType: TARGET_OPTIONS_TRANSLATE[item.targetType as EKPITargetType],
        category: CATEGORY_OPTIONS_TRANSLATE[item.category as EKPICategory],
        baseline: isNumber(item.baselineQ4) ? NumberUtil.format(item.baselineQ4, options) : TRANSLATE.NA,
        target: isNumber(item.targetQ4) ? NumberUtil.format(item.targetQ4, options) : TRANSLATE.NA,
        comment: ''
      }
    ]
  })

export const formatMetricName = (data: { metric: string; entity: EEntity }) => {
  if (EEntity.DEPARTMENT === data.entity) {
    return data.metric + ' (D)'
  }
  if (EEntity.INDIVIDUAL === data.entity) {
    return data.metric + ' (I)'
  }
  return data.metric
}
